import React, { useState, useEffect } from 'react';
import api from '../services/api';
import {
    LayoutDashboard,
    Settings,
    Globe,
    Lock,
    Save,
    CreditCard,
    MessageSquare,
    ShieldCheck,
    Smartphone,
    UserCircle,
    Bot
} from 'lucide-react';
import { cn } from '../lib/utils';
import { PageHeader } from './ui/PageHeader';

const SocietySettings: React.FC = () => {
    const [activeTab, setActiveTab] = useState('profile');
    const [soc, setSoc] = useState<any>(null);
    const [update, setUpdate] = useState<any>({});
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get('/society').then(res => {
            setSoc(res.data);
            setUpdate(res.data);
        });
    }, []);

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        try {
            await api.patch('/society', update);
            alert('Settings updated successfully');
        } catch (e) { alert('Update failed'); }
        setSaving(false);
    };

    if (!soc) return null;

    const tabs = [
        { id: 'profile', label: 'Profile', icon: UserCircle },
        { id: 'payment', label: 'Payment', icon: CreditCard },
        { id: 'communication', label: 'Communication', icon: MessageSquare },
        { id: 'ai-bots', label: 'AI & Bots', icon: Bot },
        { id: 'integrations', label: 'Integrations', icon: Globe },
    ];

    return (
        <div className="space-y-6 max-w-5xl">
            <PageHeader title="Society Settings" subtitle="Configure global parameters and integrations" />

            <div className="flex flex-col md:flex-row gap-6">
                <div className="w-full md:w-64 shrink-0 space-y-1">
                    {tabs.map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={cn(
                                "w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-all",
                                activeTab === tab.id ? "bg-white text-violet-600 shadow-sm border border-slate-200" : "text-slate-500 hover:bg-slate-100"
                            )}
                        >
                            <tab.icon size={18} />
                            {tab.label}
                        </button>
                    ))}
                </div>

                <div className="flex-1 bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                    <form onSubmit={handleSave} className="p-6">
                        {activeTab === 'profile' && (
                            <div className="space-y-4">
                                <h3 className="font-bold text-slate-900 mb-4">Society Profile</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-1">
                                        <label className="text-xs font-semibold text-slate-500">Society Name</label>
                                        <input value={update.name} onChange={e => setUpdate({...update, name: e.target.value})} className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-violet-500" />
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-xs font-semibold text-slate-500">Registration Number</label>
                                        <input value={update.registration_number || ''} onChange={e => setUpdate({...update, registration_number: e.target.value})} className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-violet-500" />
                                    </div>
                                    <div className="md:col-span-2 space-y-1">
                                        <label className="text-xs font-semibold text-slate-500">Address</label>
                                        <textarea value={update.address} onChange={e => setUpdate({...update, address: e.target.value})} className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-violet-500" rows={3} />
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === 'payment' && (
                            <div className="space-y-4">
                                <h3 className="font-bold text-slate-900 mb-4">Payment Configuration</h3>
                                <div className="space-y-4">
                                    <div className="space-y-1">
                                        <label className="text-xs font-semibold text-slate-500">UPI ID for Collections</label>
                                        <input value={update.payment_upi_id || ''} onChange={e => setUpdate({...update, payment_upi_id: e.target.value})} className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-violet-500" placeholder="society@upi" />
                                    </div>
                                    <div className="p-4 bg-amber-50 border border-amber-100 rounded-lg flex gap-3">
                                        <ShieldCheck className="text-amber-600 shrink-0" size={20} />
                                        <p className="text-xs text-amber-700 leading-relaxed">
                                            This UPI ID will be shown to residents for facility bookings and ledger payments. Ensure it is correct to avoid payment failures.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === 'communication' && (
                            <div className="space-y-4">
                                <h3 className="font-bold text-slate-900 mb-4">Communication Channels</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-1">
                                        <label className="text-xs font-semibold text-slate-500">Primary Email</label>
                                        <input value={update.primary_email || ''} onChange={e => setUpdate({...update, primary_email: e.target.value})} className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-violet-500" />
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-xs font-semibold text-slate-500">Emergency Phone</label>
                                        <input value={update.emergency_phone || ''} onChange={e => setUpdate({...update, emergency_phone: e.target.value})} className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-violet-500" />
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-xs font-semibold text-slate-500">WhatsApp Number</label>
                                        <input value={update.whatsapp_number || ''} onChange={e => setUpdate({...update, whatsapp_number: e.target.value})} className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-violet-500" />
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === 'ai-bots' && (
                            <div className="space-y-6">
                                <h3 className="font-bold text-slate-900 mb-4">AI & Automation</h3>
                                <div className="space-y-4">
                                    <label className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200 cursor-pointer">
                                        <div>
                                            <p className="text-sm font-bold text-slate-900">AI Resident Assistant</p>
                                            <p className="text-xs text-slate-500">Enable GPT-powered chatbot for residents</p>
                                        </div>
                                        <input type="checkbox" checked={update.ai_chatbot_enabled} onChange={e => setUpdate({...update, ai_chatbot_enabled: e.target.checked})} className="w-5 h-5 accent-violet-600" />
                                    </label>
                                    <label className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200 cursor-pointer">
                                        <div>
                                            <p className="text-sm font-bold text-slate-900">WhatsApp Ticketing Bot</p>
                                            <p className="text-xs text-slate-500">Allow residents to raise tickets via WhatsApp</p>
                                        </div>
                                        <input type="checkbox" checked={update.whatsapp_bot_enabled} onChange={e => setUpdate({...update, whatsapp_bot_enabled: e.target.checked})} className="w-5 h-5 accent-violet-600" />
                                    </label>
                                </div>
                            </div>
                        )}

                        {activeTab === 'integrations' && (
                            <div className="space-y-4">
                                <h3 className="font-bold text-slate-900 mb-4">External Systems</h3>
                                <div className="space-y-4">
                                    <div className="space-y-1">
                                        <label className="text-xs font-semibold text-slate-500 font-mono">BILLING_API_URL</label>
                                        <input value={update.external_api_url || ''} onChange={e => setUpdate({...update, external_api_url: e.target.value})} className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-sm font-mono outline-none focus:ring-2 focus:ring-violet-500" />
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-xs font-semibold text-slate-500 font-mono">AUTH_TOKEN</label>
                                        <input type="password" value={update.external_api_key || ''} onChange={e => setUpdate({...update, external_api_key: e.target.value})} className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-sm font-mono outline-none focus:ring-2 focus:ring-violet-500" />
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-xs font-semibold text-slate-500 font-mono">GEMINI_API_KEY</label>
                                        <input type="password" value={update.gemini_api_key || ''} onChange={e => setUpdate({...update, gemini_api_key: e.target.value})} className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-sm font-mono outline-none focus:ring-2 focus:ring-violet-500" />
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="mt-8 flex justify-end gap-3 pt-6 border-t border-slate-100">
                            <button type="button" className="px-4 py-2 text-sm font-bold text-slate-500 hover:text-slate-900 transition-colors">Cancel</button>
                            <button type="submit" disabled={saving} className="bg-violet-600 text-white px-6 py-2 rounded-lg font-bold text-sm hover:bg-violet-700 transition-all flex items-center gap-2 disabled:opacity-50">
                                {saving ? 'Saving...' : 'Save Settings'}
                                <Save size={16} />
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default SocietySettings;
